import Extent from '@arcgis/core/geometry/Extent.js';
import { getMap, getMapView, useMapStore } from '@/map/mapStore';
import {
  nextBookmarkId,
  useBookmarkStore,
  type Bookmark,
} from '@/state/bookmarkStore';
import type { Tool } from './types';

type Input = { name: string };

const RESERVED = new Set([
  '__rcmc_highlights',
  '__rcmc_user_selection',
  '__rcmc_buffers',
  '__rcmc_markers',
  '__rcmc_sketch_temp',
  '__rcmc_measure_temp',
]);

export const bookmarkSave: Tool<Input> = {
  name: 'bookmark_save',
  description:
    'Save the current map state (extent + visible feature layers + basemap) under a name.',
  input_schema: {
    type: 'object',
    properties: { name: { type: 'string' } },
    required: ['name'],
    additionalProperties: false,
  },
  execute: ({ name }) => {
    const view = getMapView();
    const map = getMap();
    if (!view || !map) throw new Error('bookmark.save: map not initialised');
    const ext = view.extent as Extent | undefined;
    if (!ext) throw new Error('bookmark.save: no current extent');

    const visible_layer_ids: string[] = [];
    map.layers.forEach((l) => {
      if (RESERVED.has(l.id)) return;
      if (l.visible) visible_layer_ids.push(l.id);
    });

    const bookmark: Bookmark = {
      id: nextBookmarkId(),
      name: name.trim() || 'Untitled bookmark',
      extent: {
        xmin: ext.xmin,
        ymin: ext.ymin,
        xmax: ext.xmax,
        ymax: ext.ymax,
        wkid: ext.spatialReference?.wkid ?? undefined,
      },
      visible_layer_ids,
      basemap_id: useMapStore.getState().basemapId,
      created_at: Date.now(),
    };
    useBookmarkStore.getState().add(bookmark);
    return { id: bookmark.id, name: bookmark.name };
  },
};
